import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import GlassButton from './GlassButton';
import './fonts.css';
import { supabase } from './supabaseClient';

function QuranAdmin() {
  // 🎯 Ultra-Conservative State Design
  const [state, setState] = useState({
    currentCompletion: null,
    allCompletions: [],
    loading: false,
    error: null,
    selectedCompletionId: null,
    targetCompletionNumber: null // برای کنترل دستی
  });

  // 🔄 Helper functions برای state management
  const setLoading = (loading) => setState(prev => ({ ...prev, loading }));
  const setError = (error) => setState(prev => ({ ...prev, error }));
  const setCurrentCompletion = (currentCompletion) => setState(prev => ({ ...prev, currentCompletion }));
  const setAllCompletions = (allCompletions) => setState(prev => ({ ...prev, allCompletions }));
  const setSelectedCompletionId = (selectedCompletionId) => setState(prev => ({ ...prev, selectedCompletionId }));
  const setTargetCompletionNumber = (targetCompletionNumber) => setState(prev => ({ ...prev, targetCompletionNumber }));

  // 📊 Get current working completion
  const getWorkingCompletion = () => {
    if (state.selectedCompletionId) {
      return state.allCompletions.find(c => c.id === state.selectedCompletionId) || state.currentCompletion;
    }
    return state.currentCompletion;
  };

  // 🎯 Manual completion number control
  const jumpToCompletion = async () => {
    const targetNumber = state.targetCompletionNumber;
    if (!targetNumber || targetNumber < 1 || targetNumber > 999) {
      setError('شماره ختم باید بین 1 تا 999 باشه');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('🎯 JUMPING TO COMPLETION:', targetNumber);

      // Check if completion already exists
      const existingCompletion = state.allCompletions.find(c => c.completion_number === targetNumber);

      if (existingCompletion) {
        // Switch to existing completion
        if (existingCompletion.status === 'completed') {
          // Make it current, make current completion completed
          const { error: updateError } = await supabase
            .from('quran_completions')
            .update({ status: 'current', completed_at: null })
            .eq('id', existingCompletion.id);

          if (updateError) throw new Error(updateError.message);

          if (state.currentCompletion) {
            const { error: completeError } = await supabase
              .from('quran_completions')
              .update({
                status: 'completed',
                completed_at: new Date().toISOString()
              })
              .eq('id', state.currentCompletion.id);

            if (completeError) throw new Error(completeError.message);
          }
        }

        await loadData();
      } else {
        // Create new completion with target number
        const { error: completeCurrentError } = await supabase
          .from('quran_completions')
          .update({
            status: 'completed',
            completed_at: new Date().toISOString()
          })
          .eq('id', state.currentCompletion.id);

        if (completeCurrentError) throw new Error(completeCurrentError.message);

        const { data: newCompletion, error: createError } = await supabase
          .from('quran_completions')
          .insert({
            status: 'current',
            completion_number: targetNumber,
            completed_parts: []
          })
          .select()
          .single();

        if (createError) throw new Error(createError.message);

        console.log('✅ CREATED NEW COMPLETION:', newCompletion);
        await loadData();
      }

      setTargetCompletionNumber(null);
      console.log('✅ JUMP COMPLETED');

    } catch (err) {
      console.error('❌ JUMP ERROR:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetAllCompletions = async () => {
    if (state.loading) return;

    const ok = window.confirm('ریست کامل: همه ختم‌ها (جاری و انجام‌شده) پاک می‌شود و از ختم شماره ۱ شروع می‌شود. ادامه می‌دهید؟');
    if (!ok) return;

    try {
      setLoading(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('quran_completions')
        .delete()
        .neq('id', 0);

      if (deleteError) throw new Error(deleteError.message);

      const { error: insertError } = await supabase
        .from('quran_completions')
        .insert({
          status: 'current',
          completion_number: 1,
          completed_parts: []
        });

      if (insertError) throw new Error(insertError.message);

      await loadData();
    } catch (err) {
      console.error('Reset all completions error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 🚀 Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all completions in one query
      const { data, error } = await supabase
        .from('quran_completions')
        .select('*')
        .order('completion_number', { ascending: false });

      if (error) throw new Error(error.message);

      console.log('📊 LOADED DATA:', data);

      // Separate current and completed
      const current = data.find(c => c.status === 'current') || null;
      const completed = data.filter(c => c.status === 'completed') || [];

      console.log('🔍 CURRENT:', current);
      console.log('🔍 COMPLETED:', completed);

      // Ensure current exists
      if (!current) {
        // Calculate next completion number properly
        const highestNumber = completed.length > 0 ? 
          Math.max(...completed.map(c => c.completion_number)) : 0;
        const nextNumber = highestNumber + 1;
        
        console.log('🔢 CREATING NEW CURRENT WITH NUMBER:', nextNumber);

        const { data: newCurrent, error: createError } = await supabase
          .from('quran_completions')
          .insert({
            status: 'current',
            completion_number: nextNumber,
            completed_parts: []
          })
          .select()
          .single();

        if (createError) throw new Error(createError.message);
        
        setCurrentCompletion(newCurrent);
        setAllCompletions([newCurrent, ...completed]);
      } else {
        setCurrentCompletion(current);
        setAllCompletions([current, ...completed]);
      }

      setSelectedCompletionId(null); // Reset to current
    } catch (err) {
      console.error('Load data error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 🎯 Ultra-Simple Toggle Function
  const togglePart = async (partNumber) => {
    // Prevent operations during loading
    if (state.loading) return;

    const workingCompletion = getWorkingCompletion();
    if (!workingCompletion) return;

    try {
      setLoading(true);
      setError(null);

      console.log('🎯 TOGGLE START:', {
        partNumber,
        completionId: workingCompletion.id,
        completionNumber: workingCompletion.completion_number,
        currentParts: workingCompletion.completed_parts
      });

      // Simple array operation - no mutations
      const currentParts = [...(workingCompletion.completed_parts || [])];
      const isCompleted = currentParts.includes(partNumber);
      
      let newParts;
      if (isCompleted) {
        // Remove part
        newParts = currentParts.filter(p => p !== partNumber);
      } else {
        // Add part
        newParts = [...currentParts, partNumber].sort((a, b) => a - b);
      }

      console.log('📝 CALCULATED PARTS:', {
        wasCompleted: isCompleted,
        oldParts: currentParts,
        newParts: newParts
      });

      // Update database
      const { data, error } = await supabase
        .from('quran_completions')
        .update({ completed_parts: newParts })
        .eq('id', workingCompletion.id)
        .select()
        .single();

      if (error) throw new Error(error.message);

      console.log('✅ DATABASE SUCCESS:', data);

      // Update state atomically
      const updatedAllCompletions = state.allCompletions.map(c => 
        c.id === data.id ? data : c
      );

      setAllCompletions(updatedAllCompletions);
      
      // Update current completion if it was the one we edited
      if (state.currentCompletion?.id === data.id) {
        setCurrentCompletion(data);
      }

      console.log('🔄 STATE UPDATED');

      // Check if completion is finished
      if (newParts.length === 30) {
        await handleCompletionFinished(data);
      }

    } catch (err) {
      console.error('❌ TOGGLE ERROR:', err);
      setError(err.message);
      // Reload data to ensure consistency
      setTimeout(() => loadData(), 1000);
    } finally {
      setLoading(false);
    }
  };

  // 🏁 Handle completion finished
  const handleCompletionFinished = async (completedCompletion) => {
    try {
      console.log('🏁 COMPLETION FINISHED:', completedCompletion.completion_number);

      // Mark as completed
      const { error: completeError } = await supabase
        .from('quran_completions')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', completedCompletion.id);

      if (completeError) throw new Error(completeError.message);

      // Calculate next completion number - FIX: Get ALL completions including the one we just completed
      const { data: allCompletionsData, error: fetchError } = await supabase
        .from('quran_completions')
        .select('completion_number')
        .order('completion_number', { ascending: false });

      if (fetchError) throw new Error(fetchError.message);

      console.log('📊 ALL COMPLETIONS FOR CALCULATION:', allCompletionsData);

      // Find the highest completion number from ALL completions
      const highestNumber = allCompletionsData && allCompletionsData.length > 0 ? 
        Math.max(...allCompletionsData.map(c => c.completion_number)) : 0;
      
      const nextNumber = highestNumber + 1;
      
      console.log('🔢 CALCULATION: highest =', highestNumber, 'next =', nextNumber);

      // Create new current completion
      const { data: newCurrent, error: createError } = await supabase
        .from('quran_completions')
        .insert({
          status: 'current',
          completion_number: nextNumber,
          completed_parts: []
        })
        .select()
        .single();

      if (createError) throw new Error(createError.message);

      console.log('🆕 NEW COMPLETION CREATED:', newCurrent.completion_number);

      // Update state
      const updatedAllCompletions = state.allCompletions.map(c => 
        c.id === completedCompletion.id ? { ...c, status: 'completed', completed_at: new Date().toISOString() } : c
      );

      setAllCompletions([newCurrent, ...updatedAllCompletions]);
      setCurrentCompletion(newCurrent);
      setSelectedCompletionId(null);

    } catch (err) {
      console.error('❌ COMPLETION ERROR:', err);
      setError(err.message);
    }
  };

  // 🔄 Simple completion switching
  const switchToCompletion = async (completion) => {
    if (state.loading) return;

    try {
      setLoading(true);
      setError(null);

      console.log('🔄 SWITCHING TO:', completion.completion_number);

      if (completion.status === 'completed') {
        // Switch to completed completion - make it current
        const { error: updateError } = await supabase
          .from('quran_completions')
          .update({ status: 'current', completed_at: null })
          .eq('id', completion.id);

        if (updateError) throw new Error(updateError.message);

        // Make current completion completed
        if (state.currentCompletion) {
          const { error: completeError } = await supabase
            .from('quran_completions')
            .update({
              status: 'completed',
              completed_at: new Date().toISOString()
            })
            .eq('id', state.currentCompletion.id);

          if (completeError) throw new Error(completeError.message);
        }

        // Reload data to get fresh state
        await loadData();
      } else {
        // Switch to current completion
        setSelectedCompletionId(null);
      }

      console.log('✅ SWITCH COMPLETED');

    } catch (err) {
      console.error('❌ SWITCH ERROR:', err);
      setError(err.message);
      setTimeout(() => loadData(), 1000);
    } finally {
      setLoading(false);
    }
  };

  // 🔄 Reset current completion
  const resetCurrentCompletion = async () => {
    const workingCompletion = getWorkingCompletion();
    if (!workingCompletion || !window.confirm('آیا از بازنشانی ختم فعلی مطمئن هستید؟')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('quran_completions')
        .update({ completed_parts: [] })
        .eq('id', workingCompletion.id)
        .select()
        .single();

      if (error) throw new Error(error.message);

      // Update state
      const updatedAllCompletions = state.allCompletions.map(c => 
        c.id === data.id ? data : c
      );

      setAllCompletions(updatedAllCompletions);
      
      if (state.currentCompletion?.id === data.id) {
        setCurrentCompletion(data);
      }

      console.log('🔄 RESET COMPLETED');

    } catch (err) {
      console.error('❌ RESET ERROR:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (state.loading) {
    return (
      <div style={{ 
        width: '100vw', 
        height: '100vh',
        backgroundColor: '#000000',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
        fontFamily: 'AbarMidFaNum, Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '10px' }}>بارگذاری...</div>
          <div style={{ fontSize: '16px', opacity: '0.7' }}>Loading...</div>
        </div>
      </div>
    );
  }

  const workingCompletion = getWorkingCompletion();
  const completedCompletions = state.allCompletions.filter(c => c.status === 'completed');

  return (
    <div style={{ 
      width: '100vw', 
      height: '100vh',
      backgroundColor: '#000000',
      position: 'relative',
      overflow: 'auto'
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(20px)',
        padding: '20px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        zIndex: 100,
        textAlign: 'center'
      }}>
        <h1 style={{ 
          fontSize: '28px', 
          margin: '0',
          color: 'white',
          fontFamily: 'AbarMidFaNum, Arial, sans-serif',
          fontWeight: 'bold'
        }}>
          📖 مدیریت ختم قرآن
        </h1>
        <p style={{ 
          fontSize: '14px', 
          margin: '5px 0 0 0',
          color: 'rgba(255,255,255,0.8)',
          fontFamily: 'AbarMidFaNum, Arial, sans-serif'
        }}>
          Quran Completion Management
        </p>
        <Link to="/" style={{ textDecoration: 'none', marginTop: '10px', display: 'inline-block', marginLeft: '10px' }}>
          <GlassButton
            style={{
              padding: '8px 16px',
              fontSize: '12px',
              background: 'rgba(255, 152, 0, 0.2)',
              border: '1px solid rgba(255, 152, 0, 0.5)',
              fontFamily: 'AbarMidFaNum, Arial, sans-serif'
            }}
          >
            🏠 بازگشت به صفحه اصلی
          </GlassButton>
        </Link>
        <Link to="/admin" style={{ textDecoration: 'none', marginTop: '10px', display: 'inline-block' }}>
          <GlassButton
            style={{
              padding: '8px 16px',
              fontSize: '12px',
              background: 'rgba(33, 150, 243, 0.2)',
              border: '1px solid rgba(33, 150, 243, 0.5)',
              fontFamily: 'AbarMidFaNum, Arial, sans-serif'
            }}
          >
            📿 مدیریت ذکرها
          </GlassButton>
        </Link>
        <Link to="/amal-admin" style={{ textDecoration: 'none', marginTop: '10px', display: 'inline-block' }}>
          <GlassButton
            style={{
              padding: '8px 16px',
              fontSize: '12px',
              background: 'rgba(0, 191, 255, 0.2)',
              border: '1px solid rgba(0, 191, 255, 0.5)',
              fontFamily: 'AbarMidFaNum, Arial, sans-serif'
            }}
          >
            🙏 مدیریت اعمال
          </GlassButton>
        </Link>
        <GlassButton
          onClick={resetAllCompletions}
          style={{
            padding: '8px 16px',
            fontSize: '12px',
            background: 'rgba(244, 67, 54, 0.2)',
            border: '1px solid rgba(244, 67, 54, 0.5)',
            fontFamily: 'AbarMidFaNum, Arial, sans-serif',
            marginTop: '10px',
            marginLeft: '10px'
          }}
        >
          🧹 ریست کل ختم‌ها
        </GlassButton>
      </div>

      {/* Main Content */}
      <div style={{
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Error Message */}
        {state.error && (
          <div style={{
            background: 'rgba(255, 0, 0, 0.1)',
            border: '1px solid rgba(255, 0, 0, 0.3)',
            borderRadius: '10px',
            padding: '15px',
            marginBottom: '20px',
            color: '#ff6b6b',
            textAlign: 'center',
            fontSize: '14px',
            fontFamily: 'AbarMidFaNum, Arial, sans-serif'
          }}>
            خطا: {state.error}
          </div>
        )}

        {/* Current Completion */}
        {workingCompletion && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            borderRadius: '15px',
            padding: '20px',
            marginBottom: '30px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                fontSize: '20px',
                color: 'white',
                margin: '0',
                fontFamily: 'AbarMidFaNum, Arial, sans-serif'
              }}>
                ختم شماره {workingCompletion.completion_number || 1}
                {state.selectedCompletionId && (
                  <span style={{
                    fontSize: '14px',
                    color: '#FFA500',
                    marginLeft: '10px'
                  }}>
                    (در حال ویرایش)
                  </span>
                )}
              </h2>
              <div style={{
                fontSize: '16px',
                color: '#4CAF50',
                fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                fontWeight: 'bold'
              }}>
                {(workingCompletion.completed_parts || []).length} / 30 جزء
              </div>
            </div>

            {/* Manual Control Panel */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              borderRadius: '10px',
              padding: '15px',
              marginBottom: '20px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{
                fontSize: '16px',
                color: 'white',
                margin: '0 0 15px 0',
                fontFamily: 'AbarMidFaNum, Arial, sans-serif'
              }}>
                🎯 کنترل دستی ختم
              </h3>
              
              <div style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <input
                  type="number"
                  min="1"
                  max="999"
                  value={state.targetCompletionNumber || ''}
                  onChange={(e) => setTargetCompletionNumber(parseInt(e.target.value) || null)}
                  placeholder="شماره ختم (1-999)"
                  disabled={state.loading}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    fontSize: '14px',
                    fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                    width: '150px',
                    opacity: state.loading ? 0.5 : 1
                  }}
                />
                
                <GlassButton
                  onClick={jumpToCompletion}
                  disabled={!state.targetCompletionNumber || state.loading}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    background: 'rgba(76, 175, 80, 0.2)',
                    border: '1px solid rgba(76, 175, 80, 0.5)',
                    fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                    opacity: (!state.targetCompletionNumber || state.loading) ? 0.5 : 1,
                    cursor: (!state.targetCompletionNumber || state.loading) ? 'not-allowed' : 'pointer'
                  }}
                >
                  🚀 برو به ختم
                </GlassButton>

                <span style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '12px',
                  fontFamily: 'AbarMidFaNum, Arial, sans-serif'
                }}>
                  (موجود: {state.allCompletions.length} ختم)
                </span>
              </div>
            </div>

            {/* Completion Switcher */}
            <div style={{
              marginBottom: '20px',
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <span style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                fontFamily: 'AbarMidFaNum, Arial, sans-serif'
              }}>
                انتخاب ختم:
              </span>
              <button
                onClick={() => setSelectedCompletionId(null)}
                disabled={state.loading}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: `2px solid ${!state.selectedCompletionId ? '#4CAF50' : 'rgba(255, 255, 255, 0.3)'}`,
                  background: !state.selectedCompletionId ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                  color: !state.selectedCompletionId ? '#4CAF50' : 'white',
                  fontSize: '12px',
                  fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                  fontWeight: 'bold',
                  cursor: state.loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: state.loading ? 0.5 : 1
                }}
              >
                فعلی
              </button>
              
              {/* Show only last 10 completions for better UX */}
              {completedCompletions.slice(0, 10).map(completion => (
                <button
                  key={completion.id}
                  onClick={() => switchToCompletion(completion)}
                  disabled={state.loading}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: `2px solid ${state.selectedCompletionId === completion.id ? '#4CAF50' : 'rgba(255, 255, 255, 0.3)'}`,
                    background: state.selectedCompletionId === completion.id ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                    color: state.selectedCompletionId === completion.id ? '#4CAF50' : 'white',
                    fontSize: '12px',
                    fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                    fontWeight: 'bold',
                    cursor: state.loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: state.loading ? 0.5 : 1
                  }}
                >
                  ختم {completion.completion_number}
                </button>
              ))}
              
              {completedCompletions.length > 10 && (
                <span style={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '12px',
                  fontFamily: 'AbarMidFaNum, Arial, sans-serif'
                }}>
                  ... +{completedCompletions.length - 10} ختم دیگر
                </span>
              )}
            </div>

            {/* Parts Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: '10px',
              marginBottom: '20px',
              direction: 'rtl'
            }}>
              {Array.from({ length: 30 }, (_, i) => i + 1).map(partNumber => {
                const completedParts = workingCompletion.completed_parts || [];
                const isCompleted = completedParts.includes(partNumber);
                
                return (
                  <button
                    key={partNumber}
                    onClick={() => togglePart(partNumber)}
                    disabled={state.loading}
                    style={{
                      padding: '15px',
                      borderRadius: '8px',
                      border: `2px solid ${isCompleted ? '#4CAF50' : 'rgba(255, 255, 255, 0.3)'}`,
                      background: isCompleted ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                      color: isCompleted ? '#4CAF50' : 'white',
                      fontSize: '14px',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                      fontWeight: 'bold',
                      cursor: state.loading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      direction: 'rtl',
                      transform: state.loading ? 'scale(0.95)' : 'scale(1)',
                      opacity: state.loading ? 0.7 : 1,
                      boxShadow: isCompleted ? '0 4px 15px rgba(76, 175, 80, 0.4)' : 'none',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      if (!state.loading) {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = isCompleted ? 
                          '0 6px 20px rgba(76, 175, 80, 0.6)' : 
                          '0 4px 15px rgba(255, 255, 255, 0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!state.loading) {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = isCompleted ? 
                          '0 4px 15px rgba(76, 175, 80, 0.4)' : 
                          'none';
                      }
                    }}
                  >
                    {/* Loading indicator */}
                    {state.loading && (
                      <div style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#FFA500',
                        animation: 'pulse 1s infinite'
                      }} />
                    )}
                    جزء {partNumber}
                  </button>
                );
              })}
            </div>

            {/* Add CSS animation */}
            <style jsx>{`
              @keyframes pulse {
                0% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.5; transform: scale(1.2); }
                100% { opacity: 1; transform: scale(1); }
              }
            `}</style>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center'
            }}>
              <GlassButton
                onClick={resetCurrentCompletion}
                disabled={state.loading}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  background: 'rgba(255, 152, 0, 0.2)',
                  border: '1px solid rgba(255, 152, 0, 0.5)',
                  fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                  opacity: state.loading ? 0.5 : 1,
                  cursor: state.loading ? 'not-allowed' : 'pointer'
                }}
              >
                🔄 بازنشانی
              </GlassButton>
            </div>
          </div>
        )}

        {/* Previous Completions */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '15px',
          padding: '20px',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <h2 style={{
            fontSize: '20px',
            color: 'white',
            marginBottom: '15px',
            fontFamily: 'AbarMidFaNum, Arial, sans-serif'
          }}>
            ختم‌های
          </h2>
          
          {completedCompletions.length === 0 ? (
            <div style={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '14px',
              fontFamily: 'AbarMidFaNum, Arial, sans-serif',
              textAlign: 'center',
              padding: '20px'
            }}>
              هنوز ختمی تکمیل نشده است
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gap: '10px'
            }}>
              {completedCompletions.map(completion => (
                <div
                  key={completion.id}
                  style={{
                    background: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: '8px',
                    padding: '15px',
                    border: '1px solid rgba(76, 175, 80, 0.3)'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      fontSize: '16px',
                      color: '#4CAF50',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                      fontWeight: 'bold'
                    }}>
                      ختم شماره {completion.completion_number}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif'
                    }}>
                      {completion.completed_at ? 
                        new Date(completion.completed_at).toLocaleDateString('fa-IR') : 
                        'تاریخ نامشخص'
                      }
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default QuranAdmin;
